import { DatePicker, FormField, Input, Select, Textarea, todayIso, useToast } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import {
  useColonies,
  useCreateColony,
  useLocations,
  useUpdateColony,
  type Colony,
  type ColonyOrigin,
  type DatePrecision,
} from '../api';

const ORIGINS: ColonyOrigin[] = ['split', 'swarm', 'purchase', 'other'];
const PRECISIONS: DatePrecision[] = ['day', 'month', 'year'];
/** Origins that come from another colony of the business (VOL-009). */
const WITH_PARENT: string[] = ['split', 'swarm'];

export interface ColonyFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Edit this colony; otherwise create one. */
  colony?: Colony;
  /** Preselected location for new colonies. */
  locationId?: string;
}

/** Create or edit a colony (VOL-001). */
export function ColonyFormDialog({ open, onClose, colony, locationId }: ColonyFormDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const locations = useLocations(false, open && !colony);
  const create = useCreateColony();
  const update = useUpdateColony();
  const mutation = colony ? update : create;
  const [form, setForm] = useState(() => ({
    name: colony?.name ?? '',
    origin_date: colony?.origin_date ?? todayIso(),
    origin_precision: colony?.origin_precision ?? ('day' as DatePrecision),
    origin: colony?.origin ?? '',
    parent_id: colony?.parent?.id ?? '',
    notes: colony?.notes ?? '',
    location_id: locationId ?? '',
  }));
  const withParent = WITH_PARENT.includes(form.origin);
  // Splits may come from colonies that are gone by now, so all statuses.
  const colonies = useColonies({ status: 'all' }, open && withParent);
  const parents = (colonies.data ?? []).filter((c) => c.id !== colony?.id);
  const locationValue = form.location_id || locations.data?.[0]?.id || '';
  const errors = formErrors(mutation.error, {
    name: ['colony_name.', 'colony.name_taken'],
    origin_date: ['colony.origin_in_future', 'colony.origin_after_relocation'],
    parent: ['colony.parent_'],
    location: ['location.'],
    notes: ['colony.notes'],
  });

  const close = () => {
    mutation.reset();
    onClose();
  };

  const submit = () => {
    const body = {
      name: form.name.trim() || null,
      origin_date: form.origin_date,
      origin_precision: form.origin_precision,
      origin: (form.origin || null) as ColonyOrigin | null,
      parent_id: (withParent && form.parent_id) || null,
      notes: form.notes.trim() || null,
    };
    if (colony) {
      update.mutate(
        { id: colony.id, body, version: colony.version },
        {
          onSuccess: () => {
            toast.show(t('apiary.colonyForm.saved'));
            close();
          },
        },
      );
    } else {
      create.mutate(
        { ...body, location_id: locationValue },
        {
          onSuccess: (created) => {
            toast.show(t('apiary.colonyForm.created'));
            close();
            navigate(`/voelker/${created.id}`);
          },
        },
      );
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      title={colony ? t('apiary.colonyForm.editTitle') : t('apiary.colonyForm.createTitle')}
      submitLabel={t('common.save')}
      onSubmit={submit}
      pending={mutation.isPending}
      error={errors.general}
    >
      <FormField
        label={t('apiary.colonyForm.name')}
        hint={colony ? undefined : t('apiary.colonyForm.nameHint')}
        error={errors.field.name}
      >
        {(p) => (
          <Input
            {...p}
            value={form.name}
            maxLength={60}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        )}
      </FormField>
      {!colony && (
        <FormField label={t('apiary.colonyForm.location')} required error={errors.field.location}>
          {(p) => (
            <Select
              {...p}
              value={locationValue}
              onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))}
              options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
            />
          )}
        </FormField>
      )}
      <div
        style={{
          display: 'grid',
          gap: 'var(--space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
        }}
      >
        <FormField
          label={t('apiary.colonyForm.originDate')}
          required
          error={errors.field.origin_date}
        >
          {(p) => (
            <DatePicker
              {...p}
              value={form.origin_date}
              max={todayIso()}
              onChange={(v) => setForm((f) => ({ ...f, origin_date: v }))}
            />
          )}
        </FormField>
        <FormField label={t('apiary.colonyForm.precision')}>
          {(p) => (
            <Select
              {...p}
              value={form.origin_precision}
              onChange={(e) =>
                setForm((f) => ({ ...f, origin_precision: e.target.value as DatePrecision }))
              }
              options={PRECISIONS.map((v) => ({ value: v, label: t(`apiary.precision.${v}`) }))}
            />
          )}
        </FormField>
      </div>
      <FormField label={t('apiary.colonyForm.origin')}>
        {(p) => (
          <Select
            {...p}
            value={form.origin}
            onChange={(e) => setForm((f) => ({ ...f, origin: e.target.value }))}
            options={[
              { value: '', label: t('apiary.origin.none') },
              ...ORIGINS.map((v) => ({ value: v, label: t(`apiary.origin.${v}`) })),
            ]}
          />
        )}
      </FormField>
      {withParent && (
        <FormField label={t('apiary.colonyForm.parent')} error={errors.field.parent}>
          {(p) => (
            <Select
              {...p}
              value={form.parent_id}
              onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
              options={[
                { value: '', label: t('apiary.colonyForm.parentUnknown') },
                ...parents.map((c) => ({
                  value: c.id,
                  label:
                    c.status === 'active'
                      ? c.name
                      : `${c.name} (${t(`apiary.status.${c.status}`)})`,
                })),
              ]}
            />
          )}
        </FormField>
      )}
      <FormField label={t('apiary.colonyForm.notes')} error={errors.field.notes}>
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        )}
      </FormField>
    </FormDialog>
  );
}
